import * as React from 'react';
import { Button } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { ActionButton, Empty, MetricCard, PageHeader, Row, RowMeta, RowText, RowTitle, Search, StatusPill, Surface, Toolbar } from '../components/ui';

const TABS = ['PENDING', 'PROCESSING', 'APPROVED', 'REJECTED', 'ALL'];
const linkStyle = { color: '#7c3aed', textDecoration: 'none', cursor: 'pointer', fontWeight: 600 };

const FAILURE_LABELS = {
    FACE_MISMATCH: 'Face doesn\'t match ID',
    LIVENESS_FAILED: 'Liveness check failed',
    BLURRY_IMAGE: 'Image too blurry',
    EXPIRED_DOCUMENT: 'Document expired',
    MISSING_DOCUMENTS: 'Documents missing',
    PROCESSING_ERROR: 'Processing error',
};

export default function VerificationQueue() {
    const [attempts, setAttempts] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [tab, setTab] = React.useState('PENDING');
    const [search, setSearch] = React.useState('');
    const [detail, setDetail] = React.useState(null);
    const [docs, setDocs] = React.useState(null);
    const [docsLoading, setDocsLoading] = React.useState(false);
    const [reviewForm, setReviewForm] = React.useState({ action: '', reason: '', notes: '' });
    const [reviewing, setReviewing] = React.useState(false);

    const load = React.useCallback(() => {
        setLoading(true);
        const statusParam = tab === 'ALL' ? '' : `?status=${tab}`;
        httpClient(`${API_BASE_URL}/verification/queue${statusParam}`)
            .then(({ json }) => {
                setAttempts(json.docs || []);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [tab]);

    React.useEffect(load, [load]);

    const loadDocuments = (attemptId) => {
        setDocsLoading(true);
        setDocs(null);
        httpClient(`${API_BASE_URL}/verification/${attemptId}/documents`)
            .then(({ json }) => setDocs(json.documents || {}))
            .catch(console.error)
            .finally(() => setDocsLoading(false));
    };

    const openDetail = (attempt) => {
        setDetail(attempt);
        setReviewForm({ action: '', reason: '', notes: '' });
        loadDocuments(attempt._id);
    };

    const handleReview = () => {
        if (!reviewForm.action) return;
        setReviewing(true);
        httpClient(`${API_BASE_URL}/verification/${detail._id}/review`, {
            method: 'POST',
            body: JSON.stringify(reviewForm),
        })
            .then(() => {
                setDetail(null);
                load();
            })
            .catch(console.error)
            .finally(() => setReviewing(false));
    };

    // ── Derived ──────────────────────────────────────────────────────────────

    const filtered = React.useMemo(() => {
        if (!search) return attempts;
        const s = search.toLowerCase();
        return attempts.filter(a => {
            const user = a.user || {};
            return (user.name || '').toLowerCase().includes(s)
                || (user.phone || '').includes(s);
        });
    }, [attempts, search]);

    const counts = React.useMemo(() => {
        const c = { PENDING: 0, PROCESSING: 0, APPROVED: 0, REJECTED: 0, ALL: 0 };
        attempts.forEach(a => { c[a.status] = (c[a.status] || 0) + 1; c.ALL++; });
        return c;
    }, [attempts]);

    // ── Render ────────────────────────────────────────────────────────────────

    if (detail) return <DetailView
        attempt={detail}
        docs={docs}
        docsLoading={docsLoading}
        reviewForm={reviewForm}
        setReviewForm={setReviewForm}
        reviewing={reviewing}
        onReview={handleReview}
        onBack={() => setDetail(null)}
    />;

    return <main className="sk-page">
        <PageHeader
            eyebrow="Trust & Safety"
            title="Verification Queue"
            description="Review identity verification attempts that were escalated after 3 auto-failures or L2 address proof submissions."
        />

        <Surface title="Pending Reviews" description="Attempts awaiting admin decision.">
            <Toolbar right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone…" />}>
                {TABS.map(t => (
                    <Button key={t} aria-pressed={tab === t} variant={tab === t ? 'contained' : 'outlined'} color="primary"
                        onClick={() => setTab(t)}>
                        {t} ({counts[t] || 0})
                    </Button>
                ))}
            </Toolbar>
        </Surface>

        {loading && <p style={{ padding: 16, color: '#94a3b8' }}>Loading…</p>}

        {!loading && filtered.length === 0 && <Empty icon="✅" title="Queue is clear" description="No verification attempts need review right now." />}

        {filtered.map(a => {
            const user = a.user || {};
            return <Row key={a._id} onClick={() => openDetail(a)}>
                <RowTitle>{user.name || 'Unknown User'}</RowTitle>
                <RowText>{user.phone || '—'}</RowText>
                <RowMeta>
                    <StatusPill status={a.status === 'PROCESSING' ? 'pending' : a.status === 'APPROVED' ? 'active' : a.status === 'REJECTED' ? 'banned' : 'pending'}>
                        {a.status}
                    </StatusPill>
                    <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 13 }}>
                        Level {a.level} · Attempt #{a.attemptNumber} · {new Date(a.createdAt).toLocaleDateString()}
                    </span>
                </RowMeta>
            </Row>;
        })}
    </main>;
}

// ── Detail View ──────────────────────────────────────────────────────────────

function DetailView({ attempt, docs, docsLoading, reviewForm, setReviewForm, reviewing, onReview, onBack }) {
    const user = attempt.user || {};
    const imgStyle = { maxWidth: 300, maxHeight: 220, borderRadius: 8, border: '1px solid #334155', objectFit: 'contain', background: '#0f172a' };

    return <main className="sk-page">
        <PageHeader
            eyebrow="Verification Review"
            title={user.name || 'Unknown User'}
            description={`${user.phone || '—'} · Level ${attempt.level} · Attempt #${attempt.attemptNumber}`}
        />

        <div style={{ padding: '0 24px 8px' }}>
            <button onClick={onBack} style={{ ...linkStyle, background: 'none', border: 'none', fontSize: 14, padding: 0 }}>
                ← Back to queue
            </button>
        </div>

        {/* User Info */}
        <Surface title="User Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, padding: 16 }}>
                <InfoCell label="Name" value={user.name} />
                <InfoCell label="Phone" value={user.phone} />
                <InfoCell label="Current Level" value={`Level ${user.verificationLevel || 0}`} />
                <InfoCell label="Member Since" value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'} />
                <InfoCell label="Attempts" value={user.verificationAttemptCount || 0} />
            </div>
        </Surface>

        {/* L2 (proof of address): why it landed here + what Didit read off the document */}
        {attempt.level === 'L2' && (
            <Surface title="Address Proof Review">
                {attempt.escalationReason && (
                    <div style={{ margin: 16, marginBottom: 0, padding: 12, borderRadius: 8, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
                        {attempt.escalationReason === 'NAME_MISMATCH'
                            ? 'Why in review: name on the document does not match the verified ID. Common for a bill in a parent\'s / spouse\'s name — check the name and address below and approve if plausible.'
                            : 'Why in review: Didit flagged this document for manual review (quality or country/address check).'}
                    </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, padding: 16 }}>
                    <InfoCell label="Verified ID Name" value={attempt.extractedName || user.nameEn || user.name || '—'} />
                    <InfoCell label="Name on Document" value={(attempt.poa && attempt.poa.nameOnDocument) || '—'} />
                    <InfoCell label="Document Type" value={(attempt.poa && attempt.poa.documentType) || '—'} />
                    <InfoCell label="Issuer" value={(attempt.poa && attempt.poa.issuer) || '—'} />
                    <InfoCell label="Issue Date" value={(attempt.poa && attempt.poa.issueDate) || '—'} />
                    <InfoCell label="Address" value={(attempt.poa && attempt.poa.formattedAddress) || '—'} />
                </div>
            </Surface>
        )}

        {/* Match Results */}
        <Surface title="Verification Results">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, padding: 16 }}>
                <InfoCell label="Face Match" value={attempt.faceMatchConfidence != null ? `${attempt.faceMatchConfidence}%` : 'N/A'} />
                <InfoCell label="Liveness Score" value={attempt.livenessScore != null ? `${attempt.livenessScore}` : 'N/A'} />
                <InfoCell label="Extracted Name" value={attempt.extractedName || '—'} />
                <InfoCell label="Document #" value={attempt.extractedDocumentNumber || '—'} />
                <InfoCell label="DOB" value={attempt.extractedDOB ? new Date(attempt.extractedDOB).toLocaleDateString() : '—'} />
                <InfoCell label="Expiry" value={attempt.extractedExpiry ? new Date(attempt.extractedExpiry).toLocaleDateString() : '—'} />
                <InfoCell label="Country" value={attempt.extractedCountry || '—'} />
            </div>

            {attempt.failureReasons && attempt.failureReasons.length > 0 && (
                <div style={{ padding: '0 16px 16px' }}>
                    <p style={{ color: '#ef4444', fontWeight: 600, marginBottom: 4 }}>Failure Reasons:</p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {attempt.failureReasons.map((r, i) => (
                            <li key={i} style={{ color: '#fbbf24', fontSize: 14 }}>
                                {FAILURE_LABELS[r] || r}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Surface>

        {/* Documents */}
        <Surface title="Submitted Documents">
            {docsLoading && <p style={{ padding: 16, color: '#94a3b8' }}>Loading documents…</p>}
            {!docsLoading && docs && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: 16 }}>
                    {docs.front && <DocImage label="ID Front" src={docs.front} style={imgStyle} />}
                    {docs.back && <DocImage label="ID Back" src={docs.back} style={imgStyle} />}
                    {docs.selfie && <DocImage label="Selfie" src={docs.selfie} style={imgStyle} />}
                    {docs.addressProof && <DocImage label="Address Proof" src={docs.addressProof} style={imgStyle} />}
                    {!docs.front && !docs.back && !docs.selfie && !docs.addressProof && (
                        <p style={{ color: '#94a3b8' }}>No documents available.</p>
                    )}
                </div>
            )}
        </Surface>

        {/* Admin Review Form */}
        {(attempt.status === 'PENDING' || attempt.status === 'PROCESSING') && (
            <Surface title="Admin Decision">
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {['APPROVE', 'REJECT', 'REQUEST_NEW_DOCS'].map(action => (
                            <Button key={action}
                                variant={reviewForm.action === action ? 'contained' : 'outlined'}
                                color={action === 'APPROVE' ? 'primary' : 'default'}
                                onClick={() => setReviewForm({ ...reviewForm, action })}
                                style={action === 'REJECT' ? { borderColor: '#ef4444', color: reviewForm.action === action ? '#fff' : '#ef4444', background: reviewForm.action === action ? '#ef4444' : 'transparent' } : {}}>
                                {action === 'APPROVE' ? '✅ Approve' : action === 'REJECT' ? '❌ Reject' : '📄 Request New Docs'}
                            </Button>
                        ))}
                    </div>

                    {(reviewForm.action === 'REJECT' || reviewForm.action === 'REQUEST_NEW_DOCS') && (
                        <textarea
                            placeholder={reviewForm.action === 'REJECT' ? 'Rejection reason (shown to user)…' : 'What documents are needed…'}
                            value={reviewForm.reason}
                            onChange={e => setReviewForm({ ...reviewForm, reason: e.target.value })}
                            style={{ background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: 12, minHeight: 80, resize: 'vertical', fontSize: 14 }}
                        />
                    )}

                    <textarea
                        placeholder="Internal notes (optional, not shown to user)…"
                        value={reviewForm.notes}
                        onChange={e => setReviewForm({ ...reviewForm, notes: e.target.value })}
                        style={{ background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: 12, minHeight: 60, resize: 'vertical', fontSize: 14 }}
                    />

                    <ActionButton onClick={onReview} disabled={!reviewForm.action || reviewing} variant="primary">
                        {reviewing ? 'Submitting…' : 'Submit Decision'}
                    </ActionButton>
                </div>
            </Surface>
        )}
    </main>;
}

// ── Small components ─────────────────────────────────────────────────────────

function InfoCell({ label, value }) {
    return <div>
        <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <p style={{ margin: '4px 0 0', fontSize: 15, color: '#e2e8f0', fontWeight: 500 }}>{value || '—'}</p>
    </div>;
}

function DocImage({ label, src, style }) {
    return <div>
        <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>{label}</span>
        <a href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt={label} style={style} />
        </a>
    </div>;
}
